import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWelcomeCarousel } from "@/hooks/use-welcome-carousel";
import { MobileNav } from "@/components/MobileNav";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Suspense } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PricingModal } from "@/components/subscription/PricingModal";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { 
  Loader2, 
  MessageCircle, 
  TrendingUp, 
  SparkleIcon, 
  ArrowUpRight, 
  CalendarDays,
  AlertTriangle,
  BarChart3,
  DollarSign,
  HelpCircle,
  History,
  Bell,
  Zap,
  ChevronRight,
  Clock,
  ChevronUp,
  ChevronDown
} from "lucide-react";

import {
  StrykrMarketScreenerCard,
  AskStrykrAICard,
  StrykrChatHistoryCard,
  StrykrCalendarCard,
  StrykrAlertsCard
} from "@/components/dashboard-strykr";

import { WelcomeCarousel } from "@/components/onboarding/WelcomeCarousel";
import { useSubscription } from "@/hooks/use-subscription";

const StrykrDashboardContent = () => {
  const navigate = useNavigate();
  const { session } = useSession();
  const isMobile = useIsMobile();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const { isCarouselOpen, setCarouselOpen } = useWelcomeCarousel();

  // Get user profile
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session?.user?.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Use the subscription context instead of making a separate API call
  const { isSubscribed: isPremium, isTrialing } = useSubscription();
  console.log('[StrykrDashboard] Subscription status:', { isPremium, isTrialing, raw: { isSubscribed: isPremium, isTrialing } });

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[StrykrDashboard] Error fetching session:', sessionError);
          throw sessionError;
        }

        if (!sessionData.session?.user) {
          console.log('[StrykrDashboard] No authenticated user found');
          navigate('/auth');
          return;
        }

        const user = sessionData.session.user;

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('trial_end_date')
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('[StrykrDashboard] Error fetching profile:', profileError);
        }

        const trialEndDate = profile?.trial_end_date ? new Date(profile.trial_end_date) : null;
        const isExpired = trialEndDate ? new Date() > trialEndDate : false;

        if (isExpired) {
          console.log('[StrykrDashboard] Trial appears expired, checking subscription status');
          
          // Add delay to prevent concurrent edge function calls
          await new Promise(resolve => setTimeout(resolve, 150));
          
          const { data, error } = await supabase.functions.invoke('check-subscription', {
          });

          if (error) {
            console.error('[StrykrDashboard] Error checking subscription:', error);
            throw error;
          }

          if (!data?.subscribed) {
            console.log('[StrykrDashboard] Subscription check failed - showing pricing modal');
            setIsTrialExpired(true);
            setShowPricingModal(true);
          }
        }
      } catch (error) {
        console.error('[StrykrDashboard] Error in access check:', error);
        if (error instanceof Error &&
            (error.message.includes('cors') ||
             error.message.includes('CORS') ||
             error.message.includes('cookie'))) {
          console.error('[StrykrDashboard] Possible CORS/cookie issue:', error.message);
        }
      }
    }

    // Delay the entire access check to let alerts load first (shortened from 200ms)
    const timer = setTimeout(checkAccess, 100);
    return () => clearTimeout(timer);
  }, [navigate]);
  
  // Navigation handlers
  const handleAskAiClick = () => navigate('/chat');
  const handleAlertsClick = () => navigate('/alerts');
  const handleCalendarClick = () => navigate('/calendar');
  const handleHistoryClick = () => navigate('/history');
  const handleSettingsClick = () => navigate('/settings');

  if (isTrialExpired && showPricingModal) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <PricingModal
          open={showPricingModal}
          onOpenChange={setShowPricingModal}
          isTrialExpired={isTrialExpired}
        />
      </div>
    );
  }

  // Staggered animation for cards
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20
      }
    }
  };
  
  // Subtle background pattern for dashboard
  const backgroundPattern = (
    <div className="fixed inset-0 z-[-1] opacity-5">
      <div className="absolute inset-0 bg-[radial-gradient(#4299e1_1px,transparent_1px)] [background-size:20px_20px]"></div>
    </div>
  );
  
  return (
    <div className="flex flex-col h-screen relative overflow-hidden bg-gradient-to-br from-black via-slate-900 to-black">
      {backgroundPattern}
      
      {/* Animated accent lights */}
      <div className="fixed top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] animate-pulse-slow"></div>
      <div className="fixed bottom-0 left-0 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] animate-pulse-slow"></div>
      
      {/* Top navigation removed for cleaner interface */}

      <div className={cn("flex-1 overflow-hidden relative z-10", isMobile && "pb-20")}>
        <ScrollArea className="h-full">
          <div className="container mx-auto py-2 sm:py-4 md:py-6 px-3 sm:px-4 lg:px-6 space-y-3 sm:space-y-4 md:space-y-6 max-w-7xl">
            {/* Enhanced welcome banner with better design and spacing */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={cn(
                "relative overflow-hidden",
                "rounded-lg md:rounded-xl",
                "border border-slate-600/30",
                "bg-gradient-to-br from-slate-900/90 via-slate-800/70 to-slate-900/90",
                "shadow-xl shadow-black/50",
                "backdrop-blur-xl",
                isMobile && "mobile-dashboard-header"
              )}
            >
              {/* Enhanced ambient lighting effects */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/6 to-cyan-500/8 pointer-events-none" />
              
              {/* Premium glass morphism overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none rounded-lg md:rounded-xl" />
              
              <div className={cn(
                "relative p-3 md:p-4",
                isMobile && "mobile-dashboard-inner"
              )}>
                {/* Compact main content layout */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  {/* User info section - more compact */}
                  <div className="flex flex-col gap-1.5">
                    {/* Smaller welcome text */}
                    <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-purple-100 tracking-tight leading-tight">
                      Welcome back, {userProfile?.email ? `${userProfile.email.split('@')[0]}` : 'Trader'}!
                    </h1>
                    
                    {/* Compact status bar */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Smaller premium status badge */}
                      {isPremium ? (
                        <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold px-1.5 py-0.5 text-xs rounded-md tracking-tight flex items-center shadow-md shadow-amber-500/20">
                          <DollarSign className="h-2.5 w-2.5 mr-0.5" />
                          PREMIUM
                        </Badge>
                      ) : isTrialing ? (
                        <Badge className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold px-1.5 py-0.5 text-xs rounded-md tracking-tight flex items-center shadow-md shadow-blue-500/20">
                          <Clock className="h-2.5 w-2.5 mr-0.5" />
                          TRIAL
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-slate-500/40 text-slate-300 font-semibold px-1.5 py-0.5 text-xs rounded-md tracking-tight flex items-center backdrop-blur-sm">
                          FREE
                        </Badge>
                      )}
                      
                      {/* Compact date */}
                      <div className="flex items-center gap-1 text-slate-400">
                        <div className="h-0.5 w-0.5 rounded-full bg-slate-500"></div>
                        <p className="text-xs">
                          {format(new Date(), 'EEE, MMM d')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* More compact Ask Strykr AI button */}
                  <Button 
                    onClick={handleAskAiClick}
                    className={cn(
                      "relative group overflow-hidden flex-shrink-0",
                      "h-9 md:h-10 px-4 md:px-5",
                      "bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700",
                      "hover:from-blue-700 hover:via-blue-800 hover:to-indigo-800",
                      "text-white font-semibold rounded-lg",
                      "shadow-lg shadow-blue-900/30 hover:shadow-xl hover:shadow-blue-900/40",
                      "transition-all duration-300 ease-out transform-gpu",
                      "hover:scale-105 active:scale-95",
                      "backdrop-blur-sm border border-blue-400/30",
                      "text-xs md:text-sm"
                    )}
                  >
                    {/* Compact button background effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/8 via-white/4 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Compact button content */}
                    <div className="relative flex items-center gap-2">
                      <div className="h-4 w-4 rounded-md bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-all duration-300">
                        <MessageCircle className="h-2.5 w-2.5 group-hover:scale-110 transition-transform duration-200" />
                      </div>
                      <span className="font-semibold tracking-tight">Ask Strykr AI</span>
                      <ArrowUpRight className="h-3 w-3 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200" />
                    </div>
                    
                    {/* Compact inner glow */}
                    <div className="absolute inset-0 rounded-lg pointer-events-none border border-white/10 shadow-inner shadow-white/5"></div>
                  </Button>
                </div>
              </div>
              
              {/* Subtle border effects */}
              <div className="absolute inset-0 rounded-lg md:rounded-xl pointer-events-none border border-white/[0.06] shadow-inner shadow-white/[0.02]" />
              
              {/* Minimal animated accent */}
              <div className="absolute top-0 left-1/3 w-1/3 h-px bg-gradient-to-r from-transparent via-blue-400/30 to-transparent"></div>
            </motion.div>

            {/* Feature navigation cards - compact and vibrant */}
            <motion.div 
              className="grid grid-cols-2 gap-2 sm:gap-2.5 md:gap-3 mb-3 sm:mb-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Alerts - Primary feature */}
              <Button
                variant="outline"
                className={cn(
                  "relative overflow-hidden group",
                  "flex items-center justify-start gap-2.5 h-14 sm:h-16",
                  "bg-gradient-to-br from-amber-950/40 via-amber-900/30 to-amber-950/50",
                  "hover:from-amber-950/60 hover:via-amber-900/50 hover:to-amber-950/70",
                  "border-amber-700/30 hover:border-amber-600/50",
                  "px-3 py-2.5 rounded-lg sm:rounded-xl",
                  "backdrop-blur-sm shadow-lg shadow-amber-900/20",
                  "hover:shadow-xl hover:shadow-amber-900/30",
                  "transition-all duration-300 ease-out transform-gpu",
                  "hover:scale-[1.02] active:scale-[0.98]"
                )}
                onClick={handleAlertsClick}
              >
                {/* Enhanced ambient glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-500/8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                
                <div className="relative flex items-center justify-start gap-2.5 w-full">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-amber-500/30 to-amber-600/40 flex items-center justify-center group-hover:from-amber-500/50 group-hover:to-amber-600/60 transition-all duration-300 shadow-md">
                    <Bell className="h-3.5 w-3.5 text-amber-400 group-hover:scale-110 transition-transform duration-200" />
                  </div>
                  <div className="text-left min-w-0 flex-1">
                    <div className="font-semibold text-sm text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-100">Market Alerts</div>
                    <div className="text-[10px] text-amber-300/70 truncate">Risk monitoring & price alerts</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-amber-400/60 group-hover:text-amber-400/90 group-hover:translate-x-0.5 transition-all duration-200" />
                </div>
                
                {/* Premium inner glow */}
                <div className="absolute inset-0 rounded-lg sm:rounded-xl pointer-events-none border border-white/[0.02] shadow-inner shadow-amber-500/[0.02]" />
              </Button>

              {/* Calendar - Secondary feature */}
              <Button
                variant="outline"
                className={cn(
                  "relative overflow-hidden group",
                  "flex items-center justify-start gap-2.5 h-14 sm:h-16",
                  "bg-gradient-to-br from-teal-950/40 via-emerald-900/30 to-teal-950/50",
                  "hover:from-teal-950/60 hover:via-emerald-900/50 hover:to-teal-950/70",
                  "border-teal-700/30 hover:border-teal-600/50",
                  "px-3 py-2.5 rounded-lg sm:rounded-xl",
                  "backdrop-blur-sm shadow-lg shadow-teal-900/20",
                  "hover:shadow-xl hover:shadow-teal-900/30",
                  "transition-all duration-300 ease-out transform-gpu",
                  "hover:scale-[1.02] active:scale-[0.98]"
                )}
                onClick={handleCalendarClick}
              >
                {/* Enhanced ambient glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-emerald-500/5 to-teal-500/8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                
                <div className="relative flex items-center justify-start gap-2.5 w-full">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-teal-500/30 to-emerald-600/40 flex items-center justify-center group-hover:from-teal-500/50 group-hover:to-emerald-600/60 transition-all duration-300 shadow-md">
                    <CalendarDays className="h-3.5 w-3.5 text-teal-400 group-hover:scale-110 transition-transform duration-200" />
                  </div>
                  <div className="text-left min-w-0 flex-1">
                    <div className="font-semibold text-sm text-transparent bg-clip-text bg-gradient-to-r from-teal-200 to-emerald-100">Economic Calendar</div>
                    <div className="text-[10px] text-teal-300/70 truncate">Market-moving events & data</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-teal-400/60 group-hover:text-teal-400/90 group-hover:translate-x-0.5 transition-all duration-200" />
                </div>
                
                {/* Premium inner glow */}
                <div className="absolute inset-0 rounded-lg sm:rounded-xl pointer-events-none border border-white/[0.02] shadow-inner shadow-teal-500/[0.02]" />
              </Button>
            </motion.div>

            {/* Main dashboard content - refined grid with proper spacing */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 md:gap-5"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* First row - Alerts takes priority (8 columns) with better visual treatment */}
              <motion.div variants={itemVariants} className="md:col-span-8 lg:row-span-1">
                <div className={cn(
                  "relative overflow-hidden group",
                  "rounded-lg sm:rounded-xl md:rounded-2xl",
                  "border border-amber-700/30 hover:border-amber-600/50",
                  "bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80",
                  "shadow-xl shadow-amber-900/20 hover:shadow-2xl hover:shadow-amber-900/30",
                  "backdrop-blur-lg transition-all duration-300 ease-out transform-gpu",
                  "hover:scale-[1.01] h-full"
                )}>
                  {/* Enhanced ambient lighting */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/8 via-orange-500/5 to-amber-500/6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  
                  {/* Glass morphism overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none" />
                  
                  <StrykrAlertsCard className="h-full relative z-10" />
                  
                  {/* Premium inner glow */}
                  <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl pointer-events-none border border-white/[0.05] shadow-inner shadow-white/[0.02]" />
                </div>
              </motion.div>
              
              {/* Calendar summary (4 columns) with matching visual style */}
              <motion.div variants={itemVariants} className="md:col-span-4 lg:row-span-1">
                <div className={cn(
                  "relative overflow-hidden group",
                  "rounded-lg sm:rounded-xl md:rounded-2xl",
                  "border border-teal-700/30 hover:border-teal-600/50",
                  "bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80",
                  "shadow-xl shadow-teal-900/20 hover:shadow-2xl hover:shadow-teal-900/30",
                  "backdrop-blur-lg transition-all duration-300 ease-out transform-gpu",
                  "hover:scale-[1.01] h-full"
                )}>
                  {/* Enhanced ambient lighting */}
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500/8 via-emerald-500/5 to-teal-500/6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  
                  {/* Glass morphism overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none" />
                  
                  <StrykrCalendarCard className="h-full relative z-10" />
                  
                  {/* Premium inner glow */}
                  <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl pointer-events-none border border-white/[0.05] shadow-inner shadow-white/[0.02]" />
                </div>
              </motion.div>
              
              {/* Second row - AI Assistant (full width but more compact) */}
              <motion.div variants={itemVariants} className="md:col-span-12">
                <div className={cn(
                  "relative overflow-hidden group",
                  "rounded-lg sm:rounded-xl md:rounded-2xl",
                  "border border-blue-700/30 hover:border-blue-600/50",
                  "bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80",
                  "shadow-xl shadow-blue-900/20 hover:shadow-2xl hover:shadow-blue-900/30",
                  "backdrop-blur-lg transition-all duration-300 ease-out transform-gpu",
                  "hover:scale-[1.005]"
                )}>
                  {/* Enhanced ambient lighting */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/8 via-indigo-500/5 to-blue-500/6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  
                  {/* Glass morphism overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none" />
                  
                  <AskStrykrAICard 
                    onQuestionClick={handleAskAiClick} 
                    className="h-full relative z-10" 
                  />
                  
                  {/* Premium inner glow */}
                  <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl pointer-events-none border border-white/[0.05] shadow-inner shadow-white/[0.02]" />
                </div>
              </motion.div>
              
              {/* Market Screener with proper card styling */}
              <motion.div variants={itemVariants} className="md:col-span-6 lg:col-span-5">
                <div className={cn(
                  "relative overflow-hidden group",
                  "rounded-lg sm:rounded-xl md:rounded-2xl",
                  "border border-indigo-700/30 hover:border-indigo-600/50",
                  "bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80",
                  "shadow-xl shadow-indigo-900/20 hover:shadow-2xl hover:shadow-indigo-900/30",
                  "backdrop-blur-lg transition-all duration-300 ease-out transform-gpu",
                  "hover:scale-[1.01] h-full"
                )}>
                  {/* Enhanced ambient lighting */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/8 via-purple-500/5 to-indigo-500/6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  
                  {/* Glass morphism overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none" />
                  
                  <StrykrMarketScreenerCard className="h-full relative z-10" />
                  
                  {/* Premium inner glow */}
                  <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl pointer-events-none border border-white/[0.05] shadow-inner shadow-white/[0.02]" />
                </div>
              </motion.div>
              
              {/* Chat history with matching visual treatment */}
              <motion.div variants={itemVariants} className="md:col-span-6 lg:col-span-7">
                <div className={cn(
                  "relative overflow-hidden group",
                  "rounded-lg sm:rounded-xl md:rounded-2xl",
                  "border border-purple-700/30 hover:border-purple-600/50",
                  "bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80",
                  "shadow-xl shadow-purple-900/20 hover:shadow-2xl hover:shadow-purple-900/30",
                  "backdrop-blur-lg transition-all duration-300 ease-out transform-gpu",
                  "hover:scale-[1.01] h-full"
                )}>
                  {/* Enhanced ambient lighting */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/8 via-violet-500/5 to-purple-500/6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  
                  {/* Glass morphism overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none" />
                  
                  <StrykrChatHistoryCard className="relative z-10" />
                  
                  {/* Premium inner glow */}
                  <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl pointer-events-none border border-white/[0.05] shadow-inner shadow-white/[0.02]" />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </ScrollArea>
      </div>

      {isMobile && (
        <div className="relative z-20">
          <MobileNav />
        </div>
      )}
      
      {/* Welcome Carousel */}
      <WelcomeCarousel open={isCarouselOpen} onOpenChange={setCarouselOpen} />
    </div>
  );
};

const StrykrDashboard = () => {
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="flex h-[80vh] items-center justify-center bg-gradient-to-br from-black via-slate-900 to-black relative overflow-hidden">
            {/* Enhanced ambient lighting effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/8 via-purple-500/5 to-cyan-500/6 pointer-events-none" />
            
            <div className={cn(
              "flex flex-col items-center gap-6 relative",
              "p-8 md:p-12 rounded-2xl md:rounded-3xl",
              "border border-slate-600/40",
              "bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80",
              "shadow-2xl shadow-black/50 backdrop-blur-xl",
              "transform-gpu"
            )}>
              {/* Premium glass morphism overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none rounded-2xl md:rounded-3xl" />
              
              <div className="relative flex flex-col items-center gap-6">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full blur-xl bg-gradient-to-r from-blue-600/30 to-purple-600/30 animate-pulse"></div>
                  <Loader2 className="h-12 w-12 md:h-16 md:w-16 animate-spin text-blue-400 relative z-10" />
                </div>
                <div className="text-center space-y-2">
                  <p className="text-lg md:text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-white to-purple-200">
                    Loading your financial dashboard...
                  </p>
                  <p className="text-sm text-slate-400 bg-gradient-to-r from-slate-400 to-slate-500 bg-clip-text text-transparent">
                    Preparing market insights and analytics
                  </p>
                </div>
              </div>
              
              {/* Premium inner border glow */}
              <div className="absolute inset-0 rounded-2xl md:rounded-3xl pointer-events-none border border-white/[0.05] shadow-inner shadow-white/[0.02]" />
            </div>
          </div>
        }
      >
        <StrykrDashboardContent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default StrykrDashboard;
